// @ts-check
import { PENCIL_ICON_SVG, TRASH_ICON_SVG } from '../../shared/icons.js';

import { UNCATEGORIZED_CATEGORY, isDefaultCategory } from '../../shared/catalog.js';
import { matchesSearch } from '../../shared/search-text.js';
import { escapeHtml } from './html.js';

/**
 * Sentinel option values shared by every category and tag dropdown.
 * Selecting one runs an action instead of changing the field value.
 */
export const CATALOG_ACTIONS = {
  addCategory: '__add_category__',
  manageCategories: '__manage_categories__',
  addTag: '__add_tag__',
  manageTags: '__manage_tags__'
};

/**
 * Builds category options plus the Add and Manage action groups.
 *
 * @param {string[]} names
 * @param {boolean} [withAllOption] - Adds the "All categories" filter option.
 * @returns {string}
 */
export function categoryOptionsHtml(names, withAllOption = false) {
  const sorted = [...names].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  const options = sorted
    .map((name) => '<option value="' + escapeHtml(name) + '">' + escapeHtml(name) + '</option>')
    .join('');
  return (withAllOption ? '<option value="All">All categories</option>' : '') +
    '<optgroup label="Categories">' + options + '</optgroup>' +
    '<optgroup label="Add"><option value="' + CATALOG_ACTIONS.addCategory + '">+ Add new category…</option></optgroup>' +
    '<optgroup label="Manage"><option value="' + CATALOG_ACTIONS.manageCategories + '">Manage categories…</option></optgroup>';
}

/**
 * Category a new image should start on.
 *
 * Whatever the Images tab is filtered to is almost always what the admin is about
 * to add, so the filter carries over. With no filter the fallback is the seeded
 * catch-all rather than the first option in the dropdown, which is alphabetical
 * and would silently file everything under "Animals".
 *
 * @param {unknown} filterValue - Current value of the `#category-filter` select.
 * @param {string[]} [available] - Selectable category names; when given, a filter
 *   naming a category that is not among them is ignored.
 * @returns {string}
 */
export function defaultUploadCategory(filterValue, available = []) {
  const filtered = String(filterValue ?? '').trim();
  const isRealCategory = filtered &&
    filtered !== 'All' &&
    !Object.values(CATALOG_ACTIONS).includes(filtered) &&
    (available.length === 0 || available.includes(filtered));

  return isRealCategory ? filtered : UNCATEGORIZED_CATEGORY;
}

/**
 * Builds the existing-tag picker options plus the Add and Manage action groups.
 *
 * @param {string[]} names
 * @returns {string}
 */
export function tagOptionsHtml(names) {
  const sorted = [...names].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  const options = sorted
    .map((name) => '<option value="' + escapeHtml(name) + '">#' + escapeHtml(name) + '</option>')
    .join('');
  return '<option value="">Add existing tag…</option>' +
    (sorted.length > 0 ? '<optgroup label="Tags">' + options + '</optgroup>' : '') +
    '<optgroup label="Add"><option value="' + CATALOG_ACTIONS.addTag + '">+ Add new tag…</option></optgroup>' +
    '<optgroup label="Manage"><option value="' + CATALOG_ACTIONS.manageTags + '">Manage tags…</option></optgroup>';
}

/** How many tag names the autocomplete shows before the Add and Manage actions. */
export const TAG_SUGGEST_LIMIT = 5;

/**
 * Tag names that match a search, capped so a long catalog stays a short list.
 * An empty query returns the first names in alphabetical order.
 *
 * @param {string[]} names
 * @param {unknown} query
 * @param {number} [limit]
 * @returns {string[]}
 */
export function matchingTagNames(names, query, limit = TAG_SUGGEST_LIMIT) {
  const sorted = [...new Set(names.map((name) => String(name || '').trim()).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  const matched = sorted.filter((name) => matchesSearch([name], query));
  const cap = Number.isFinite(limit) && limit > 0 ? limit : sorted.length;
  return matched.slice(0, cap);
}

/**
 * Autocomplete rows: matching tags first, then Add and Manage, always last.
 *
 * @param {string[]} names
 * @param {unknown} query
 * @returns {Array<{ kind: 'tag'|'action', value: string, label: string }>}
 */
export function tagSuggestItems(names, query) {
  const tags = matchingTagNames(names, query).map((name) => ({
    kind: /** @type {'tag'} */ ('tag'),
    value: name,
    label: '#' + name
  }));
  return [
    ...tags,
    { kind: /** @type {'action'} */ ('action'), value: CATALOG_ACTIONS.addTag, label: 'Add new tag…' },
    { kind: /** @type {'action'} */ ('action'), value: CATALOG_ACTIONS.manageTags, label: 'Manage tags' }
  ];
}

/**
 * Renders an icon-only catalog action button with an accessible name.
 *
 * @param {string} classes - Handler class plus optional style modifiers.
 * @param {string} glyph
 * @param {string} label - Accessible name, also used as the tooltip.
 * @param {string} id
 * @param {string} name
 * @returns {string}
 */
export function catalogIconButtonHtml(classes, glyph, label, id, name) {
  return '<button type="button" class="icon-btn ' + classes + '"' +
    ' data-id="' + escapeHtml(id) + '" data-name="' + escapeHtml(name) + '"' +
    ' aria-label="' + escapeHtml(label) + '" title="' + escapeHtml(label) + '">' + glyph + '</button>';
}

/**
 * Renders one catalog list row. Seeded categories cannot be deleted, so they
 * show a "Built-in" marker instead of a delete button.
 *
 * @param {'category'|'tag'} kind
 * @param {{id: string, name: string}} item
 * @returns {string}
 */
export function catalogRowHtml(kind, item) {
  const isCategory = kind === 'category';
  const display = isCategory ? escapeHtml(item.name) : '#' + escapeHtml(item.name);
  const renameBtn = catalogIconButtonHtml(
    isCategory ? 'rename-category-btn' : 'rename-tag-btn',
    PENCIL_ICON_SVG,
    'Rename ' + kind + ' ' + item.name,
    item.id,
    item.name
  );
  const locked = isCategory && isDefaultCategory(item.name);
  const trailing = locked
    ? '<span class="catalog-locked-hint" title="Bundled category — cannot be deleted">Built-in</span>'
    : catalogIconButtonHtml(
      (isCategory ? 'delete-category-btn' : 'delete-tag-btn') + ' icon-btn-danger',
      TRASH_ICON_SVG,
      'Delete ' + kind + ' ' + item.name,
      item.id,
      item.name
    );

  return '<li>' +
    '<span class="catalog-item-name">' + display + '</span>' +
    '<div class="catalog-item-actions">' + renameBtn + trailing + '</div>' +
    '</li>';
}
